import React, { useEffect, useState } from 'react';
import { Box, Button, TextField } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setDefaultAccount } from '../../Redux/AccountSlice';
import { useSelector } from 'react-redux';
import { SessionSorageInstance } from "../../Services/SessionStorageService";
import AccountService from '../../Services/http/AccountService';
import { CircularProgress } from '@material-ui/core';
import './enterAmazonUrl.scss';
import { AmazonURLStatus } from '../Enum/AmazonURLStatus';
import Header from '../Header/header';
import OnboardingLayout from '../Layout/OnboardingLayout';
import { useTranslation } from 'react-i18next';
import DownloadPdfButton from './DownloadPdfButton';
import DownloadIcon from "@mui/icons-material/Download";
import { UserRolesEnum } from '../../Models/user';
import { AMAZON_URL_PROCESS_PDF } from './constants';

const EnterAmazonUrl = () => {
    const { t } = useTranslation("enterAmazonUrl");
    const [url, setUrl] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isSaving, setIsSaving] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionLoadingApprove, setActionLoadingApprove] = useState(false);
    const [actionLoadingComplete, setActionLoadingComplete] = useState(false);
    const defaultAccount = useSelector((state: any) => state.account.default_account);
    const userRol = useSelector((state: any) => state.general.userRol);
    const params = useParams();
    const routeAccountId = params.id;
    const allAccounts = useSelector((state: any) => state.account.account_list);

    const urlError = url.trim() === "" ? t("errors.urlRequired") : "";

    useEffect(() => {
        if (!routeAccountId) {
            navigate('/layout');
            return;
        }
        dispatch(setDefaultAccount(allAccounts.find((a) => a.id === Number(routeAccountId))));
    }, [routeAccountId, allAccounts]);

    useEffect(() => {
        if (defaultAccount && defaultAccount.id && routeAccountId !== defaultAccount.id)
            navigate(`/enterAmazonUrl/${defaultAccount.id}`, { replace: true });
        if (defaultAccount?.amazon_authorized_url) {
            setUrl(defaultAccount.amazon_authorized_url);
        }
        else {
            setUrl('');
        }
    }, [defaultAccount?.id]);

    const changeDefaultAccountAndNavigation = (action: AmazonURLStatus) => {
        const updatedAccount = {
            ...defaultAccount,
            amazon_authorized_url: url.trim(),
            amazon_authorized_url_status: action,
        };
        dispatch(setDefaultAccount(updatedAccount));
        SessionSorageInstance.setDefaultAccount(updatedAccount);
        navigate('/layout');
    };
    const onSave = async () => {
        setError('');
        if (urlError) {
            setError(t("errors.fixBeforeSaving"));
            return;
        }
        try {
            setIsSaving(true);
            const res = await AccountService.setAccountUrl(defaultAccount.id, url.trim());
            if (res && res.message === 'success') {
                changeDefaultAccountAndNavigation(AmazonURLStatus.SUBMITTED);
            } else {
                setError(t("errors.couldNotSave"));
            }
        }
        catch (e: any) {
            setError(e?.message || t("errors.networkTryAgain"));
        }
        finally {
            setIsSaving(false);
        }
    };

    const changeStatus = async (action: AmazonURLStatus) => {
        action === AmazonURLStatus.APPROVED ? setActionLoadingApprove(true) : setActionLoading(true);
        try {
            const res = await AccountService.handleAccountUrlReview(defaultAccount.id, action);
            if (res && res.message === 'success') {
                changeDefaultAccountAndNavigation(action);
            } else {
                setError(
                    t("errors.couldNotReview", {
                        action: action === AmazonURLStatus.APPROVED ? t("actions.approve") : t("actions.reject"),
                    })
                );
            }
        } catch (e: any) {
            setError(e?.message || t("errors.network"));
        } finally {
            setActionLoading(false);
        }
    };

    const completeOnboarding = async () => {
        setActionLoadingComplete(true);
        try {
            const res = await AccountService.completeAmazonUrlOnboarding(defaultAccount.id);
            if (res && res.message === 'success') {
                changeDefaultAccountAndNavigation(AmazonURLStatus.COMPLETED);
            } else {
                if (res && res.message) {
                    setError(res.message);
                    return;
                }
                setError(t("errors.couldNotComplete"));
            }
        } catch (e: any) {
            setError(e?.message || t("errors.network"));
        } finally {
            setActionLoadingComplete(false);
        }
    };

    return (
        <OnboardingLayout header={<Header open={false} />} showLanguageSelector={false} rightClassName='enter-amazon-right text-center pr-3 pl-3 pb-2 m-auto f-18' >
            <Box className="enter-amazon-inner" sx={{ width: '100%', maxWidth: 800 }}>
                <Box className="enter-amazon-url">
                    <h2 style={{ marginTop: 0 }}>{t("title")}</h2>
                    <p>{t("subtitle")}</p>
                    <Box className="video-container">
                        <Box className="video-wrapper">
                            <video controls playsInline>
                                <source src="/media/AccountSetUp.mp4" type="video/mp4" />
                                {t("video.fallback")}
                            </video>
                        </Box>
                    </Box>
                    <TextField
                        label={t("fields.amazonUrl.label")}
                        fullWidth
                        required
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        variant="outlined"
                        error={url == ''}
                        helperText={urlError || ' '}
                        FormHelperTextProps={{ sx: { minHeight: 24, m: 0 } }}
                    />

                    {error && <Box sx={{ color: 'error.main', mt: 1 }}>{error}</Box>}

                    <Box className="enter-amazon-actions">
                        <Button
                            variant="contained"
                            onClick={onSave}
                            startIcon={isSaving ? <CircularProgress size={16} /> : null}
                            disabled={isSaving}
                        >
                            {isSaving ? t("buttons.saving") : t("buttons.save")}
                        </Button>

                        {userRol == UserRolesEnum.admin && (
                            <>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => changeStatus(AmazonURLStatus.APPROVED)}
                                    startIcon={actionLoadingApprove ? <CircularProgress size={16} /> : null}
                                    disabled={actionLoadingApprove}
                                >
                                    {t("buttons.approve")}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => changeStatus(AmazonURLStatus.REJECTED)}
                                    startIcon={actionLoading ? <CircularProgress size={16} /> : null}
                                    disabled={actionLoading}
                                >
                                    {t("buttons.reject")}
                                </Button>
                            </>
                        )}
                        {(userRol == UserRolesEnum.analyst || userRol == UserRolesEnum.admin)
                            && (
                                <Button
                                    variant="outlined"
                                    color="success"
                                    onClick={() => completeOnboarding()}
                                    startIcon={actionLoadingComplete ? <CircularProgress size={16} /> : null}
                                    disabled={actionLoadingComplete}>
                                    {t("buttons.complete")}
                                </Button>)
                        }

                        <DownloadPdfButton
                            href={AMAZON_URL_PROCESS_PDF}
                            variant="outlined"
                            color="primary"
                            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                            startIcon={<DownloadIcon color="primary" />}
                        />
                    </Box>
                </Box>
            </Box>
        </OnboardingLayout>
    );
};

export default EnterAmazonUrl;